/**
 * Editorial homepage background video: HLS on desktop, MP4 on mobile.
 */
(function () {
  const HLS_SCRIPT = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.17/dist/hls.min.js';
  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');

  /**
   * @returns {boolean}
   */
  function isMobileViewport() {
    return MOBILE_MQ.matches;
  }

  /**
   * @param {string} src
   * @returns {Promise<typeof Hls | null>}
   */
  function loadHlsLibrary(src) {
    if (window.Hls) {
      return Promise.resolve(window.Hls);
    }

    return new Promise(function (resolve, reject) {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () {
        resolve(window.Hls || null);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * @param {HTMLVideoElement} video
   */
  function lockVideoControls(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('x5-playsinline', '');
    video.setAttribute('x5-video-player-type', 'h5');
    video.setAttribute('x5-video-player-fullscreen', 'false');
    video.removeAttribute('controls');
    video.setAttribute('controlslist', 'nodownload noplaybackrate nofullscreen noremoteplayback');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');
    video.setAttribute('tabindex', '-1');

    video.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
    });

    video.addEventListener('contextmenu', function (event) {
      event.preventDefault();
    });

    video.addEventListener('pause', function () {
      if (document.visibilityState === 'visible') {
        video.play().catch(function () {});
      }
    });

    if (typeof video.webkitSetPresentationMode === 'function') {
      video.addEventListener('webkitbeginfullscreen', function (event) {
        event.preventDefault();
        video.webkitSetPresentationMode('inline');
      });
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  function tryPlay(video) {
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement | null} media
   */
  function markVideoReady(video, media) {
    video.classList.remove('is-buffering');
    video.classList.add('is-ready');
    if (media) {
      media.classList.add('is-video-ready');
    }
    tryPlay(video);
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement | null} media
   */
  function bindReadyEvents(video, media) {
    const onReady = function () {
      markVideoReady(video, media);
    };

    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });
    video.addEventListener('playing', onReady, { once: true });

    video.addEventListener(
      'error',
      function () {
        video.classList.remove('is-buffering');
      },
      { once: true }
    );

    window.setTimeout(function () {
      if (!video.classList.contains('is-ready')) {
        markVideoReady(video, media);
      }
    }, 4000);
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {string} mp4Src
   * @param {HTMLElement | null} media
   */
  function loadMp4(video, mp4Src, media) {
    if (!mp4Src) {
      video.classList.remove('is-buffering');
      return;
    }

    if (!video.getAttribute('src')) {
      video.src = mp4Src;
    }

    if (!video.querySelector('source')) {
      const source = document.createElement('source');
      source.src = mp4Src;
      source.type = 'video/mp4';
      video.appendChild(source);
    }

    video.preload = isMobileViewport() ? 'metadata' : 'auto';
    video.load();
    bindReadyEvents(video, media);
    tryPlay(video);
  }

  /**
   * @param {HTMLVideoElement} video
   * @param {string} hlsSrc
   * @param {string} mp4Src
   * @param {HTMLElement | null} media
   */
  function loadHls(video, hlsSrc, mp4Src, media) {
    video.removeAttribute('src');
    video.classList.add('is-buffering');

    const canPlayNativeHls =
      video.canPlayType('application/vnd.apple.mpegurl') ||
      video.canPlayType('application/x-mpegURL');

    if (canPlayNativeHls) {
      video.src = hlsSrc;
      video.preload = 'auto';
      video.load();
      bindReadyEvents(video, media);

      video.addEventListener(
        'error',
        function () {
          loadMp4(video, mp4Src, media);
        },
        { once: true }
      );

      tryPlay(video);
      return;
    }

    loadHlsLibrary(HLS_SCRIPT)
      .then(function (Hls) {
        if (!Hls || !Hls.isSupported()) {
          loadMp4(video, mp4Src, media);
          return;
        }

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          startLevel: -1,
        });

        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          bindReadyEvents(video, media);
          tryPlay(video);
        });

        hls.on(Hls.Events.ERROR, function (_event, data) {
          if (!data || !data.fatal) return;
          hls.destroy();
          loadMp4(video, mp4Src, media);
        });

        hls.loadSource(hlsSrc);
        hls.attachMedia(video);
        tryPlay(video);
      })
      .catch(function () {
        loadMp4(video, mp4Src, media);
      });
  }

  function initEditorialVideos() {
    const videos = document.querySelectorAll('[data-editorial-video]');

    videos.forEach(function (video) {
      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      const media = video.closest('[data-editorial-media]');
      const hlsSrc = (video.dataset.hlsSrc || '').trim();
      const mp4Src = (video.dataset.mp4Src || '').trim();

      lockVideoControls(video);
      video.classList.remove('is-mobile-poster-only');
      if (media) {
        media.classList.remove('is-mobile-poster-only');
      }

      if (video.dataset.editorialVideoInit === 'true') {
        tryPlay(video);
        return;
      }

      video.dataset.editorialVideoInit = 'true';

      if (isMobileViewport()) {
        loadMp4(video, mp4Src, media);
        return;
      }

      if (hlsSrc && hlsSrc.includes('.m3u8')) {
        loadHls(video, hlsSrc, mp4Src, media);
        return;
      }

      if (!video.classList.contains('is-ready')) {
        video.classList.add('is-buffering');
      }

      bindReadyEvents(video, media);
      tryPlay(video);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEditorialVideos);
  } else {
    initEditorialVideos();
  }

  document.addEventListener('shopify:section:load', initEditorialVideos);
  document.addEventListener('shopify:section:select', initEditorialVideos);
})();
